/**
 * PROJECT STYLE — TRIM & EAVES (2026-10-07, plan docs/bim-plan/23-project-style.md §1 "Trim &
 * Exterior"; Matt: "Go on number 1"). The project's (and a firm style's) defaults for the trim
 * around every door and window, inside and out, and for every roof's eaves. Settings key
 * `trimStyle`; every field optional — unset = each element's own, exactly as before.
 *
 * The same LIVE inheritance as the rest of the style: an element's own stored value wins; else
 * (openings) the type's stored value; else the project's; else the built-in default.
 *   interiorTrim   every door / window's interior casing style (a gate never)
 *   ext / int casing width + head height   the casing sizes (interior has no per-type sizes, so the
 *                  project's are its only override)
 *   eaves          boxed or open, soffit sloped or flat, tails + their profile, fascia + its size,
 *                  gutters + their profile and size — for every roof that hasn't set its own
 */
import type { ParamValue } from './types'
import { INTERIOR_TRIM_STYLES, type InteriorTrimStyle } from './exteriorTrim'
import { FASCIA_SIZES, GUTTER_PROFILES, GUTTER_SIZES, TAIL_PROFILES, TAIL_SIZES, FRIEZE_SIZES, OUTLOOKER_SIZES } from './roofFraming'

export interface EaveStyle {
  eaveMode: 'boxed' | 'open' | null
  soffitStyle: 'sloped' | 'flat' | null
  tails: boolean | null
  tailStyle: string | null
  fascia: boolean | null
  fasciaSize: string | null
  gutters: boolean | null
  gutterProfile: string | null
  gutterSize: string | null
  // ROOF SETTINGS CLEANUP (2026-10-08, Matt: "Ideally I can do most settings through the project
  // settings"): every eave setting a roof carries can now come from the project
  eaveCrop: string | null
  deckingThicknessIn: number | null
  soffitReturnFt: number | null
  tailSize: string | null
  tailSpacingIn: number | null
  frieze: boolean | null
  friezeSize: string | null
  barge: boolean | null
  outlookerSize: string | null
  outlookerSpacingIn: number | null
  outlookerLayout: string | null
  outlookerCount: number | null
  outlookerStyle: string | null
  bargeSize: string | null
  downspoutShape: string | null
  downspoutBend: string | null
  downspoutEnd: string | null
}
export interface TrimStyleSettings {
  interiorTrim: InteriorTrimStyle | null
  extCasingFt: number | null
  extHeadFt: number | null
  intCasingFt: number | null
  intHeadFt: number | null
  /** INTERIOR DOOR FRAMES (2026-10-08): the jamb stock thickness for doors in interior walls
   *  (null = each type's own frame). When set the jamb runs finish face to finish face. */
  intDoorJambFt: number | null
  /** how far the jamb stands proud of the finish face when there's no casing (0 = flush) */
  intDoorProudFt: number | null
  /** with a casing: how much of the jamb's edge shows inside the casing (the reveal) */
  intCasingRevealFt: number | null
  eaves: EaveStyle
  /** DECORATIVE BOX BEAMS (2026-10-08): every ceiling's box beam finish (material id) unless the
   *  ceiling sets its own — null = plain wood. Their color is the palette's Box Beams role. */
  boxBeamFinish: string | null
  /** ROOFS & EAVES (2026-10-08): the project's standard roof pitch (rise per 12), a shed roof's
   *  own (null = the standard pitch), and the standard overhang — each beats the roof TYPE's
   *  default; a roof's own value (and a per-edge one) still wins. null = the type's, as before. */
  roofPitchRise: number | null
  shedPitchRise: number | null
  roofOverhangFt: number | null
}

export const DEFAULT_EAVE_STYLE: EaveStyle = {
  eaveMode: null, soffitStyle: null, tails: null, tailStyle: null, fascia: null, fasciaSize: null,
  gutters: null, gutterProfile: null, gutterSize: null,
  eaveCrop: null, deckingThicknessIn: null, soffitReturnFt: null, tailSize: null, tailSpacingIn: null,
  frieze: null, friezeSize: null, barge: null, outlookerSize: null, outlookerSpacingIn: null, outlookerLayout: null, outlookerCount: null, outlookerStyle: null,
  bargeSize: null, downspoutShape: null, downspoutBend: null, downspoutEnd: null,
}

/**
 * The project eave setting → the roof INSTANCE param it fills, one row each (the single map every
 * reader uses: apply, the overrides report, the follow-the-project cleanup and the panel badges).
 */
type EaveKind = { kind: 'choice'; ok: readonly string[] } | { kind: 'bool' } | { kind: 'num'; min: number; max: number }
export const ROOF_EAVE_FIELDS: { eave: keyof EaveStyle; inst: string; spec: EaveKind }[] = [
  { eave: 'eaveMode', inst: 'eaveMode', spec: { kind: 'choice', ok: ['boxed', 'open'] } },
  { eave: 'soffitStyle', inst: 'soffitStyle', spec: { kind: 'choice', ok: ['sloped', 'flat'] } },
  { eave: 'eaveCrop', inst: 'eaveStructureDepthIn', spec: { kind: 'choice', ok: ['', '3.5', '5.5', '7.25', '9.25', '11.25'] } },
  { eave: 'deckingThicknessIn', inst: 'deckingThicknessIn', spec: { kind: 'num', min: 0.25, max: 3 } },
  { eave: 'soffitReturnFt', inst: 'soffitReturnFt', spec: { kind: 'num', min: 0, max: 8 } },
  { eave: 'tails', inst: 'tailsDefault', spec: { kind: 'bool' } },
  { eave: 'tailStyle', inst: 'tailStyle', spec: { kind: 'choice', ok: TAIL_PROFILES.map((t) => t.key) } },
  { eave: 'tailSize', inst: 'tailSize', spec: { kind: 'choice', ok: TAIL_SIZES.map((t) => t.key) } },
  { eave: 'tailSpacingIn', inst: 'tailSpacingIn', spec: { kind: 'num', min: 12, max: 72 } },
  { eave: 'frieze', inst: 'friezeDefault', spec: { kind: 'bool' } },
  { eave: 'friezeSize', inst: 'friezeSize', spec: { kind: 'choice', ok: FRIEZE_SIZES.map((t) => t.key) } },
  { eave: 'fascia', inst: 'fasciaDefault', spec: { kind: 'bool' } },
  { eave: 'fasciaSize', inst: 'fasciaSize', spec: { kind: 'choice', ok: FASCIA_SIZES.map((f) => f.key) } },
  { eave: 'gutters', inst: 'gutterDefault', spec: { kind: 'bool' } },
  { eave: 'gutterProfile', inst: 'gutterProfile', spec: { kind: 'choice', ok: GUTTER_PROFILES.map((g) => g.key) } },
  { eave: 'gutterSize', inst: 'gutterSize', spec: { kind: 'choice', ok: GUTTER_SIZES.map((g) => g.key) } },
  { eave: 'downspoutShape', inst: 'downspoutShape', spec: { kind: 'choice', ok: ['auto', 'round', 'rect'] } },
  { eave: 'downspoutBend', inst: 'downspoutBend', spec: { kind: 'choice', ok: ['radius', 'miter'] } },
  { eave: 'downspoutEnd', inst: 'downspoutEnd', spec: { kind: 'choice', ok: ['grade', 'subdrain'] } },
  { eave: 'barge', inst: 'bargeDefault', spec: { kind: 'bool' } },
  { eave: 'outlookerSize', inst: 'outlookerSize', spec: { kind: 'choice', ok: OUTLOOKER_SIZES.map((t) => t.key) } },
  { eave: 'outlookerLayout', inst: 'outlookerLayout', spec: { kind: 'choice', ok: ['spacing', 'count'] } },
  { eave: 'outlookerSpacingIn', inst: 'outlookerSpacingIn', spec: { kind: 'num', min: 12, max: 192 } },
  { eave: 'outlookerCount', inst: 'outlookerCount', spec: { kind: 'num', min: 1, max: 24 } },
  { eave: 'outlookerStyle', inst: 'outlookerStyle', spec: { kind: 'choice', ok: TAIL_PROFILES.map((t) => t.key) } },
  { eave: 'bargeSize', inst: 'bargeSize', spec: { kind: 'choice', ok: ['', ...TAIL_SIZES.map((t) => t.key)] } },
]
const normEave = (v: unknown, s: EaveKind): string | number | boolean | null => {
  if (s.kind === 'bool') return typeof v === 'boolean' ? v : null
  if (s.kind === 'num') return typeof v === 'number' && isFinite(v) && v >= s.min && v <= s.max ? v : null
  return typeof v === 'string' && s.ok.includes(v) ? v : null
}
export const DEFAULT_TRIM_STYLE: TrimStyleSettings = {
  interiorTrim: null, extCasingFt: null, extHeadFt: null, intCasingFt: null, intHeadFt: null,
  intDoorJambFt: null, intDoorProudFt: null, intCasingRevealFt: null, eaves: DEFAULT_EAVE_STYLE,
  boxBeamFinish: null,
  roofPitchRise: null, shedPitchRise: null, roofOverhangFt: null,
}

const size = (v: unknown, max: number): number | null => (typeof v === 'number' && isFinite(v) && v > 0 ? Math.min(v, max) : null)
const nonNeg = (v: unknown, max: number): number | null => (typeof v === 'number' && isFinite(v) && v >= 0 ? Math.min(v, max) : null)
const pitch = (v: unknown): number | null => (typeof v === 'number' && isFinite(v) && v >= 0.25 && v <= 24 ? v : null)
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null)
const pick = <T extends string>(v: unknown, ok: readonly T[]): T | null => (typeof v === 'string' && (ok as readonly string[]).includes(v) ? (v as T) : null)

export function normalizeTrimStyle(raw: unknown): TrimStyleSettings {
  const r = (raw || {}) as Partial<TrimStyleSettings>
  const e = (r.eaves || {}) as Partial<EaveStyle>
  return {
    interiorTrim: pick(r.interiorTrim, INTERIOR_TRIM_STYLES),
    extCasingFt: size(r.extCasingFt, 1), extHeadFt: size(r.extHeadFt, 1.5),
    intCasingFt: size(r.intCasingFt, 1), intHeadFt: size(r.intHeadFt, 1.5),
    intDoorJambFt: size(r.intDoorJambFt, 0.5),
    intDoorProudFt: nonNeg(r.intDoorProudFt, 0.25),
    intCasingRevealFt: nonNeg(r.intCasingRevealFt, 0.1),
    eaves: Object.fromEntries(ROOF_EAVE_FIELDS.map((f) => [f.eave, normEave((e as Record<string, unknown>)[f.eave], f.spec)])) as unknown as EaveStyle,
    boxBeamFinish: typeof r.boxBeamFinish === 'string' && r.boxBeamFinish ? r.boxBeamFinish : null,
    roofPitchRise: pitch(r.roofPitchRise), shedPitchRise: pitch(r.shedPitchRise),
    roofOverhangFt: nonNeg(r.roofOverhangFt, 10),
  }
}

const blank = (v: unknown) => v === undefined || v === null || v === ''

/** a door / window's trim from the project (in place); `inst` / `typeRaw` = STORED params */
export function applyOpeningTrimStyle(
  params: Record<string, ParamValue>, inst: Record<string, ParamValue>, typeRaw: Record<string, ParamValue>,
  t: TrimStyleSettings, isGate: boolean,
): { interiorTrim?: boolean; trimSizes?: boolean } {
  const f: { interiorTrim?: boolean; trimSizes?: boolean } = {}
  if (isGate) return f
  if (t.interiorTrim && blank(inst.interiorTrim)) { params.interiorTrim = t.interiorTrim; f.interiorTrim = true }
  if (t.extCasingFt != null && blank(inst.trimWidthFt) && blank(typeRaw.trimWidthFt)) { params.trimWidthFt = t.extCasingFt; f.trimSizes = true }
  if (t.extHeadFt != null && blank(inst.trimHeadFt) && blank(typeRaw.trimHeadFt)) { params.trimHeadFt = t.extHeadFt; f.trimSizes = true }
  // the interior casing has no per-type sizes: the project's ride on the resolved params
  if (t.intCasingFt != null) params.__intTrimWidthFt = t.intCasingFt
  if (t.intHeadFt != null) params.__intTrimHeadFt = t.intHeadFt
  return f
}

/** a roof's eaves from the project (in place), for each field the roof hasn't stored itself */
export function applyRoofEaveStyle(params: Record<string, ParamValue>, inst: Record<string, ParamValue>, e: EaveStyle): boolean {
  let any = false
  const set = (key: string, v: ParamValue | null) => {
    if (v === null || !(inst[key] === undefined || inst[key] === null)) return
    params[key] = v
    any = true
  }
  for (const f of ROOF_EAVE_FIELDS) set(f.inst, e[f.eave] as ParamValue | null)
  return any
}

/** the reveal a casing leaves on the jamb's edge when the project hasn't set one: 3/16" */
export const DEFAULT_CASING_REVEAL_FT = 3 / 16 / 12
const CASED = new Set(['traditional', 'pictureFrame'])

/**
 * INTERIOR DOOR FRAMES (2026-10-08, Matt: "set the frame thickness of interior doors as a project
 * standard … 2" thick solid wood for the frames which then just act as the trims … flush with
 * the interior finish or carried forward a bit … If we're using a casing, we want the frame to
 * end on the backside of the casing with the casing slightly overlapping the outer edge of the
 * frame"). For a door in an INTERIOR wall, when the project sets a jamb thickness and the door
 * hasn't picked its own frame product: the jamb is that thick and runs finish face to finish face
 * (`hostT` = the wall's full thickness, finishes included, so paneling or tile in the wall
 * assembly deepens it); with no casing it stands `intDoorProudFt` proud of each face; with a
 * casing it ends flush with the finish and the casing laps over its edge, leaving the reveal.
 * Stamps (read by resolveOpeningFrame + the trim builders): __intJambFt, __intJambDepthFt,
 * __casingOverlapFt. In place; `inst` = STORED instance params.
 */
export function applyInteriorDoorFrame(
  params: Record<string, ParamValue>, inst: Record<string, ParamValue>, t: TrimStyleSettings, hostT: number,
): boolean {
  const jamb = t.intDoorJambFt
  if (jamb == null || !(hostT > 0)) return false
  if (typeof inst.frameProduct === 'string' && inst.frameProduct.trim()) return false
  const cased = CASED.has(String(params.interiorTrim || ''))
  const proud = cased ? 0 : (t.intDoorProudFt ?? 0)
  params.__intJambFt = jamb
  params.__intJambDepthFt = Math.round((hostT + 2 * proud) * 1e6) / 1e6
  if (blank(inst.frameMount)) params.frameMount = 'centered'
  const reveal = t.intCasingRevealFt ?? DEFAULT_CASING_REVEAL_FT
  if (cased && jamb > reveal) params.__casingOverlapFt = jamb - reveal
  return true
}

/**
 * ROOFS & EAVES (2026-10-08, Matt: "set standard pitches and overhangs for the project"): a roof
 * that hasn't stored its own slope / overhang takes the project's — the shed pitch for a shed roof
 * (else the standard pitch). In place, onto the instance keys the roof solver reads first
 * (`slopeRise ?? type.defaultSlopeRise`, `overhangFt ?? type.overhangFt`), so the project beats the
 * type's default and per-edge values still win. `inst` = STORED instance params.
 */
export function applyRoofStandards(
  params: Record<string, ParamValue>, inst: Record<string, ParamValue>, isShed: boolean, t: TrimStyleSettings,
): boolean {
  let any = false
  const p = isShed ? (t.shedPitchRise ?? t.roofPitchRise) : t.roofPitchRise
  if (p != null && blank(inst.slopeRise)) { params.slopeRise = p; any = true }
  if (t.roofOverhangFt != null && blank(inst.overhangFt)) { params.overhangFt = t.roofOverhangFt; any = true }
  return any
}

/**
 * ROOF SETTINGS CLEANUP (2026-10-08): which of a roof's STORED params override a value the PROJECT
 * defines — each row the instance key, the roof's own value and the project's. The pitch and
 * overhang standards count too. `inst` = the roof's stored params.
 */
export function roofProjectOverrides(inst: Record<string, ParamValue>, t: TrimStyleSettings): { key: string; own: ParamValue; project: ParamValue }[] {
  const out: { key: string; own: ParamValue; project: ParamValue }[] = []
  for (const f of ROOF_EAVE_FIELDS) {
    const pv = t.eaves[f.eave]
    if (pv === null || pv === undefined) continue
    const own = inst[f.inst]
    if (own === undefined || own === null) continue
    out.push({ key: f.inst, own, project: pv as ParamValue })
  }
  if (t.roofPitchRise != null && typeof inst.slopeRise === 'number') out.push({ key: 'slopeRise', own: inst.slopeRise, project: t.roofPitchRise })
  if (t.roofOverhangFt != null && typeof inst.overhangFt === 'number') out.push({ key: 'overhangFt', own: inst.overhangFt, project: t.roofOverhangFt })
  return out
}

/** the instance keys the project can set on a roof (eaves + pitch + overhang) */
export const ROOF_PROJECT_KEYS = new Set<string>([...ROOF_EAVE_FIELDS.map((f) => f.inst), 'slopeRise', 'overhangFt'])

/** the per-EDGE eave choices (node.edge) that override a roof's "on all eaves" settings */
export const ROOF_EDGE_EAVE_KEYS = ['tails', 'fascia', 'gutter', 'barge', 'frieze', 'flatSoffit'] as const

